import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SIGNUP_URL } from '../constants.js';
import { useTranslation } from 'react-i18next';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isCertva, pokLogoDark } from '../utils/domainUtils.js';

import { useLocalizedPath } from '../hooks/useLocalizedPath.jsx';
import LangMenu from './ui/LangMenu.jsx';
import { getLoginUrl } from '../utils/region-cookie.js';
import { PURPLE, FG_2, LINE } from './sections/theme.js';
import { CERTIFIER_ROOT } from '../endpoints.js';
import { TrackedEvent } from '../utils/analytics.js';

const PURPLE_HOVER = '#3F1C84';

function DropdownItem({ label, desc, href, external }) {
    return (
        <Box
            component="a"
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener' } : {})}
            sx={{
                display: 'block',
                px: 2,
                py: 1.5,
                borderRadius: '10px',
                textDecoration: 'none',
                transition: 'background 0.12s',
                '&:hover': { background: '#f8f4ff' },
            }}
        >
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e1e1e', lineHeight: 1.3 }}>
                {label}
            </Typography>
            {desc && (
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.4, lineHeight: 1.5 }}>{desc}</Typography>
            )}
        </Box>
    );
}

function DropdownSection({ title, children }) {
    return (
        <Box sx={{ mb: 1 }}>
            {title && (
                <Typography
                    sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.07em',
                        px: 2,
                        pb: 0.5,
                    }}
                >
                    {title}
                </Typography>
            )}
            {children}
        </Box>
    );
}

function NavGroupDesktop({ group, isActive, onOpen, onClose, onForceClose }) {
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const handleTriggerKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (isActive) {
                onForceClose();
            } else {
                onOpen();
                setTimeout(() => panelRef.current?.querySelector('a')?.focus(), 20);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            onOpen();
            setTimeout(() => panelRef.current?.querySelector('a')?.focus(), 20);
        } else if (e.key === 'Escape') {
            onForceClose();
        }
    };

    const handlePanelKeyDown = (e) => {
        const items = [...(panelRef.current?.querySelectorAll('a') ?? [])];
        const idx = items.indexOf(document.activeElement);
        if (e.key === 'Escape') {
            onForceClose();
            triggerRef.current?.focus();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            items[Math.min(idx + 1, items.length - 1)]?.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (idx <= 0) {
                onForceClose();
                triggerRef.current?.focus();
            } else items[idx - 1]?.focus();
        } else if (e.key === 'Tab') {
            onForceClose();
        }
    };

    return (
        <Box sx={{ position: 'relative' }} onMouseEnter={onOpen} onMouseLeave={onClose}>
            <Box
                ref={triggerRef}
                component="button"
                aria-expanded={isActive}
                aria-haspopup="menu"
                onKeyDown={handleTriggerKeyDown}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    px: 1,
                    py: 0.75,
                    borderRadius: '6px',
                    color: isActive ? PURPLE : FG_2,
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    transition: 'color 0.12s',
                    '&:hover': { color: PURPLE },
                }}
            >
                {group.label}
                <Box
                    component="span"
                    sx={{
                        fontSize: '0.55rem',
                        display: 'inline-block',
                        transition: 'transform 0.15s',
                        transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                        mt: '2px',
                    }}
                >
                    ▾
                </Box>
            </Box>

            <Box
                ref={panelRef}
                role="menu"
                inert={!isActive || undefined}
                onKeyDown={handlePanelKeyDown}
                sx={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
                    p: 1.5,
                    minWidth: group.width ?? (group.cols === 2 ? 560 : 260),
                    maxHeight: 'calc(100vh - 72px)',
                    overflowY: 'auto',
                    zIndex: 1300,
                    opacity: isActive ? 1 : 0,
                    pointerEvents: isActive ? 'auto' : 'none',
                    transform: isActive ? 'translateY(0)' : 'translateY(-8px)',
                    transition: 'opacity 0.15s, transform 0.15s',
                }}
                onMouseEnter={onOpen}
                onMouseLeave={onClose}
            >
                {group.featured && (
                    <Box
                        sx={{
                            background: '#f8f4ff',
                            borderRadius: '12px',
                            pt: 1.25,
                            pb: 0.5,
                            mb: 1,
                            '& a:hover': { background: '#fff' },
                        }}
                    >
                        <DropdownSection title={group.featured.title}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${group.featured.items.length}, 1fr)`,
                                }}
                            >
                                {group.featured.items.map((item) => (
                                    <DropdownItem key={item.href} {...item} />
                                ))}
                            </Box>
                        </DropdownSection>
                    </Box>
                )}
                {group.sections ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${group.cols}, 1fr)`, gap: 1, pt: 0.5 }}>
                        {group.sections.map((section) => (
                            <DropdownSection key={section.title} title={section.title}>
                                {section.items.map((item) => (
                                    <DropdownItem key={item.href} {...item} />
                                ))}
                            </DropdownSection>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: group.cols === 2 ? '1fr 1fr' : '1fr', pt: 0.5 }}>
                        {group.items.map((item) => (
                            <DropdownItem key={item.href} {...item} />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function NavGroupMobile({ group, isOpen, onToggle }) {
    return (
        <Box>
            <Box
                component="button"
                onClick={onToggle}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    py: 1.5,
                    px: 0,
                    color: '#1e1e1e',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    textAlign: 'left',
                }}
            >
                {group.label}
                <Box
                    component="span"
                    sx={{
                        fontSize: '0.65rem',
                        display: 'inline-block',
                        transition: 'transform 0.15s',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                >
                    ▾
                </Box>
            </Box>
            <Collapse in={isOpen}>
                <Stack sx={{ pl: 1.5, pb: 1 }}>
                    {[
                        ...(group.featured?.items ?? []),
                        ...(group.sections ? group.sections.flatMap((s) => s.items) : group.items),
                    ].map((item) => (
                        <Box
                            key={`${item.label}-${item.href}`}
                            component="a"
                            href={item.href}
                            {...(item.external ? { target: '_blank', rel: 'noopener' } : {})}
                            sx={{
                                py: 0.9,
                                color: '#475569',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                textDecoration: 'none',
                                '&:hover': { color: PURPLE },
                            }}
                        >
                            {item.label}
                        </Box>
                    ))}
                </Stack>
            </Collapse>
        </Box>
    );
}

export default function NavBar({ children }) {
    const { t } = useTranslation();
    const paths = useLocalizedPath();
    const navLogoSrc = isCertva ? `/images/${pokLogoDark()}` : '/images/pok-logo.svg';

    const [mobileOpen, setMobileOpen] = useState(false);
    const [loginHref, setLoginHref] = useState(CERTIFIER_ROOT + '/region-selector');
    const [scrolled, setScrolled] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);
    const [mobileOpenGroup, setMobileOpenGroup] = useState(null);
    const closeTimer = useRef(null);

    useEffect(() => {
        setLoginHref(getLoginUrl(CERTIFIER_ROOT));
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const openMenu = useCallback((name) => {
        clearTimeout(closeTimer.current);
        setActiveMenu(name);
    }, []);

    const closeMenu = useCallback(() => {
        closeTimer.current = setTimeout(() => setActiveMenu(null), 150);
    }, []);

    const forceClose = useCallback(() => {
        clearTimeout(closeTimer.current);
        setActiveMenu(null);
    }, []);

    const navGroups = [
        {
            id: 'funcionalidades',
            label: t('commons.navbar.features'),
            cols: 3,
            width: 840,
            featured: {
                title: t('commons.navbar.products.label'),
                items: [
                    {
                        label: t('products.family.credentials.title'),
                        desc: t('commons.navbar.products.credentials_desc'),
                        href: paths.features(),
                    },
                    {
                        label: t('products.skillsGraphs.name'),
                        desc: t('commons.navbar.products.skillsGraphs_desc'),
                        href: paths.skillsGraphs(),
                    },
                    {
                        label: t('products.skillsIntelligence.name'),
                        desc: t('commons.navbar.products.skillsIntelligence_desc'),
                        href: paths.skillsIntelligence(),
                    },
                ],
            },
            sections: [
                {
                    title: t('commons.navbar.sections.plataforma'),
                    items: [
                        {
                            label: t('commons.navbar.designer'),
                            desc: t('commons.navbar.desc.designer'),
                            href: paths.designer(),
                        },
                        {
                            label: t('commons.navbar.massive'),
                            desc: t('commons.navbar.desc.massive'),
                            href: paths.massive(),
                        },
                        {
                            label: t('commons.navbar.integrations'),
                            desc: t('commons.navbar.desc.integrations'),
                            href: paths.integrations(),
                        },
                        {
                            label: t('commons.navbar.verification'),
                            desc: t('commons.navbar.desc.verification'),
                            href: paths.verification(),
                        },
                    ],
                },
                {
                    title: t('commons.navbar.sections.avanzado'),
                    items: [
                        {
                            label: t('commons.navbar.whiteLabel'),
                            desc: t('commons.navbar.desc.whiteLabel'),
                            href: paths.whiteLabel(),
                        },
                        {
                            label: t('commons.navbar.signature'),
                            desc: t('commons.navbar.desc.signature'),
                            href: paths.signature(),
                        },
                        {
                            label: t('commons.navbar.learningPaths'),
                            desc: t('commons.navbar.desc.learningPaths'),
                            href: paths.learningPaths(),
                        },
                        {
                            label: t('commons.navbar.campaigns'),
                            desc: t('commons.navbar.desc.campaigns'),
                            href: paths.campaigns(),
                        },
                        {
                            label: t('commons.navbar.lookup'),
                            desc: t('commons.navbar.desc.lookup'),
                            href: paths.lookup(),
                        },
                    ],
                },
                {
                    title: t('commons.navbar.sections.industrias'),
                    items: [
                        { label: t('commons.navbar.iee'), desc: t('commons.navbar.desc.iee'), href: paths.iee() },
                        {
                            label: t('commons.navbar.employability'),
                            desc: t('commons.navbar.desc.employability'),
                            href: paths.employability(),
                        },
                        {
                            label: t('commons.navbar.marketing'),
                            desc: t('commons.navbar.desc.marketing'),
                            href: paths.marketing(),
                        },
                    ],
                },
            ],
        },
        {
            id: 'credenciales',
            label: t('commons.navbar.groups.credenciales'),
            cols: 1,
            width: 280,
            items: [
                { label: t('commons.navbar.dc'), desc: t('commons.navbar.desc.dc'), href: paths.dc() },
                { label: t('commons.navbar.mc'), desc: t('commons.navbar.desc.mc'), href: paths.mc() },
                { label: t('commons.navbar.ob'), desc: t('commons.navbar.desc.ob'), href: paths.ob() },
            ],
        },
        {
            id: 'comparativas',
            label: t('commons.navbar.groups.comparativas'),
            cols: 1,
            width: 240,
            items: [
                { label: 'POK vs Credly', href: paths.compareCredly() },
                { label: 'POK vs Accredible', href: paths.compareAccredible() },
                { label: 'POK vs Parchment (Badgr)', href: paths.compareBadgr() },
                { label: 'POK vs Acreditta', href: paths.compareAcreditta() },
                { label: 'POK vs Certifier', href: paths.compareCertifier() },
            ],
        },
        {
            id: 'recursos',
            label: t('commons.navbar.resources'),
            cols: 1,
            width: 220,
            items: [
                { label: t('commons.navbar.blog'), href: paths.blog() },
                { label: t('commons.navbar.podcast'), href: paths.podcast() },
                { label: 'FAQs', href: paths.faqs() },
                { label: t('commons.navbar.documentation'), href: 'https://docs.pok.tech/', external: true },
                { label: t('commons.navbar.team'), href: paths.team() },
            ],
        },
    ];

    const drawer = (
        <Box sx={{ mx: 3, mt: 3, pb: 10, position: 'relative', height: '100%' }}>
            <Box sx={{ mb: 4 }}>
                <a href={paths.home()} onClick={() => setMobileOpen(false)}>
                    <img style={{ height: 32, width: 'auto', display: 'block' }} src={navLogoSrc} alt="POK" />
                </a>
            </Box>
            <Stack divider={<Divider sx={{ borderColor: '#f1f5f9' }} />}>
                {navGroups.map((group) => (
                    <NavGroupMobile
                        key={group.id}
                        group={group}
                        isOpen={mobileOpenGroup === group.id}
                        onToggle={() => setMobileOpenGroup(mobileOpenGroup === group.id ? null : group.id)}
                    />
                ))}
                <Box
                    component="a"
                    href={paths.prices()}
                    sx={{
                        py: 1.5,
                        color: '#1e1e1e',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        textDecoration: 'none',
                        display: 'block',
                    }}
                    onClick={() => setMobileOpen(false)}
                >
                    {t('commons.navbar.prices')}
                </Box>
            </Stack>
            <Stack
                spacing={1.5}
                sx={{
                    mt: 4,
                }}
            >
                <Button
                    component="a"
                    href={`${paths.prices()}#contact`}
                    data-track={TrackedEvent.SCHEDULE_DEMO}
                    variant="contained"
                    fullWidth
                    sx={{
                        textTransform: 'none',
                        background: PURPLE,
                        color: 'white',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': { background: PURPLE_HOVER, boxShadow: 'none' },
                    }}
                >
                    {t('commons.button.schedule_demo')}
                </Button>
                <Button
                    component="a"
                    href={SIGNUP_URL}
                    data-track={TrackedEvent.START_REGISTRATION}
                    target="_blank"
                    variant="outlined"
                    fullWidth
                    sx={{ textTransform: 'none', color: PURPLE, borderColor: PURPLE, fontWeight: 600, borderRadius: 2 }}
                >
                    {t('commons.navbar.signup')}
                </Button>
                <Button
                    href={loginHref}
                    data-track={TrackedEvent.LOGIN}
                    component="a"
                    target="_blank"
                    variant="text"
                    fullWidth
                    sx={{ textTransform: 'none', color: '#475569', fontWeight: 600 }}
                >
                    {t('commons.navbar.login')}
                </Button>
            </Stack>
            <Box sx={{ position: 'absolute', bottom: 16, left: 0 }} onClick={(e) => e.stopPropagation()}>
                <LangMenu />
            </Box>
        </Box>
    );

    const container = typeof window !== 'undefined' ? () => window.document.body : undefined;

    return (
        <>
            <AppBar
                position="sticky"
                component="nav"
                aria-label={t('commons.navbar.aria_label')}
                sx={{
                    backgroundColor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.92)',
                    backdropFilter: scrolled ? 'saturate(180%) blur(16px)' : 'saturate(180%) blur(8px)',
                    WebkitBackdropFilter: scrolled ? 'saturate(180%) blur(16px)' : 'saturate(180%) blur(8px)',
                    boxShadow: scrolled ? '0 6px 28px rgba(26,15,58,0.12)' : 'none',
                    borderBottom: `1px solid ${LINE}`,
                    transition: 'background-color 0.2s, box-shadow 0.2s, backdrop-filter 0.2s',
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: { xs: 64, md: 72 },
                        maxWidth: 1200,
                        width: '100%',
                        mx: 'auto',
                        px: { xs: 2, md: 3 },
                    }}
                >
                    {/* Mobile */}
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            alignItems: 'center',
                            width: '100%',
                            justifyContent: 'space-between',
                            py: 0.5,
                        }}
                    >
                        <a href={paths.home()}>
                            <img style={{ height: 32, width: 'auto', display: 'block' }} src={navLogoSrc} alt="POK" />
                        </a>
                        <Button
                            onClick={() => setMobileOpen(true)}
                            aria-label="open drawer"
                            sx={{ minWidth: 'auto', p: 1 }}
                        >
                            <img style={{ height: 24, width: 24 }} src="/images/menu_icon.webp" alt="Menu" />
                        </Button>
                    </Box>

                    {/* Desktop */}
                    <Stack
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                        direction="row"
                    >
                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <a href={paths.home()} style={{ display: 'flex', alignItems: 'center', marginRight: 16 }}>
                                <img
                                    style={{ height: 34, width: 'auto', display: 'block' }}
                                    src={navLogoSrc}
                                    alt="POK"
                                />
                            </a>
                            {navGroups.map((group) => (
                                <NavGroupDesktop
                                    key={group.id}
                                    group={group}
                                    isActive={activeMenu === group.id}
                                    onOpen={() => openMenu(group.id)}
                                    onClose={closeMenu}
                                    onForceClose={forceClose}
                                />
                            ))}
                            <Box
                                component="a"
                                href={paths.prices()}
                                sx={{
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: '6px',
                                    color: FG_2,
                                    fontSize: '0.9375rem',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': { color: PURPLE },
                                }}
                            >
                                {t('commons.navbar.prices')}
                            </Box>
                            <span>
                                <LangMenu />
                            </span>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                href={loginHref}
                                data-track={TrackedEvent.LOGIN}
                                target="_blank"
                                component="a"
                                variant="text"
                                sx={{
                                    textTransform: 'none',
                                    color: FG_2,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    '&:hover': { color: PURPLE, background: 'transparent' },
                                }}
                            >
                                {t('commons.navbar.login')}
                            </Button>
                            <Button
                                component="a"
                                href={SIGNUP_URL}
                                data-track={TrackedEvent.START_REGISTRATION}
                                target="_blank"
                                variant="outlined"
                                sx={{
                                    textTransform: 'none',
                                    color: PURPLE,
                                    border: `2px solid ${PURPLE}`,
                                    borderRadius: 2,
                                    px: 2.2,
                                    fontSize: 15,
                                    fontWeight: 700,
                                    '&:hover': { border: `2px solid ${PURPLE}`, background: 'rgba(81,36,166,0.06)' },
                                }}
                            >
                                {t('commons.navbar.signup')}
                            </Button>
                            <Button
                                href={`${paths.prices()}#contact`}
                                data-track={TrackedEvent.SCHEDULE_DEMO}
                                component="a"
                                variant="contained"
                                sx={{
                                    background: PURPLE,
                                    color: 'white',
                                    borderRadius: 2,
                                    px: 2.2,
                                    fontSize: 15,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': { background: PURPLE_HOVER, boxShadow: 'none' },
                                }}
                            >
                                {t('commons.button.schedule_demo')}
                            </Button>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>

            <Drawer
                container={container}
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 300 },
                }}
            >
                {drawer}
            </Drawer>

            <Stack component="main">{children}</Stack>
        </>
    );
}
